import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Loader2, RefreshCw, AlertTriangle, Check, X } from 'lucide-react';
import { IgnoreControl, fetchIgnoreReasons, unignoreInvoice } from './ActualCost2Ignore';
import { linkBrokerInvoice } from '../services/brokerInvoiceLookup';
import { isBrokerJobNumber } from './actualCost2Parse';

/**
 * ActualCost2Links — CHA-361 (inverts the CHA-356/359 lookup-driven list per operator: "show all
 * commercial invoices that I show as received, what job number they have, and if they have
 * freight — the old way was too much noise").
 *
 * RECEIVED-DRIVEN: one row per in-scope received commercial invoice with its resolved job#
 * (inline-editable — the empty ones are the worklist), how it linked (exact / alias), and whether
 * that job has ACE and freight data yet. Setting a job# writes a `lookup_broker_invoice` doc keyed
 * on the ERP spelling (an exact link from then on); freight itself arrives per job via the List-2
 * upload — this tab only shows which jobs still lack it. The dashboard picks edits up on its next
 * Run; the Power BI mirror on the hourly syncAc2Lookups tick.
 */

const money = (v) => (v == null ? '—' : `$${Math.round(Number(v)).toLocaleString()}`);

const FILTERS = [
  ['missing-job', 'Missing job#'],
  ['no-freight', 'No freight'],
  ['no-ace', 'No ACE'],
  ['ignored', 'Ignored'], // CHA-379 — dismissed invoices (reversible)
  ['all', 'All'],
];

export default function ActualCost2Links({ tenantId }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('missing-job'); // the edit worklist by default
  const [search, setSearch] = useState('');
  const [editInv, setEditInv] = useState(null); // comm_inv being edited
  const [jobInput, setJobInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [ignoreReasons, setIgnoreReasons] = useState(null); // CHA-379 — from AC2 Settings
  useEffect(() => { fetchIgnoreReasons(tenantId).then(setIgnoreReasons); }, [tenantId]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const fn = httpsCallable(getFunctions(), 'getAc2Links', { timeout: 150000 });
      const res = await fn({});
      setRows(res.data?.invoices || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveJob = async (inv, row) => {
    if (saving) return; // Enter key repeat must not fire concurrent saves (self-review P3)
    const job = jobInput.trim().toUpperCase();
    if (!job) return;
    // Mirror the backend lookup shape guard (B-series or numeric) — a bad-shape job# would be
    // written and then silently DROPPED by loadLookup on the next fetch (self-review P2).
    if (!isBrokerJobNumber(job.split(/[/ ]/)[0])) {
      setError(`"${job}" doesn't look like a broker job# (B-series or numeric, e.g. B00341999).`);
      return;
    }
    setSaving(true); setError(null);
    try {
      const aliasInvoice = row?.link_via === 'alias' && row.lookup_spelling && job !== row.job_number
        ? row.lookup_spelling
        : null;
      await linkBrokerInvoice(tenantId, {
        commercialInvoice: inv,
        jobNumber: job,
        source: 'links',
        aliasInvoice,
      });
      // Rebinding an alias-linked row: the C-Air-spelling doc still points at the OLD job, whose
      // reconciliation would keep an unconsumed key and falsely flip partial-receipts (Codex P2 on
      // #269). Retarget it to the new job (both spellings name the same physical invoice; keeping
      // the doc — vs deleting — preserves any sibling spelling that resolves through it, and
      // canonicalKeyWithin collapses the within-job twins).
      await load(); // job / ACE / freight columns may change — refetch
      // Reset the form only on SUCCESS — a transient failure must not discard the typed job#.
      setEditInv(null); setJobInput('');
    } catch (e) {
      setError(`Save failed for ${inv}: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // CHA-379 — un-ignore: the invoice returns to the missing worklist (and container-bridge seeding).
  const unignore = async (inv) => {
    setError(null);
    try {
      await unignoreInvoice(inv, tenantId);
      await load();
    } catch (e) {
      setError(`Un-ignore failed for ${inv}: ${e.message}`);
    }
  };

  const counts = useMemo(() => {
    const c = { 'missing-job': 0, 'no-freight': 0, 'no-ace': 0, ignored: 0, all: (rows || []).length };
    for (const r of rows || []) {
      // CHA-379 — ignored invoices leave the missing-job worklist; they get their own bucket.
      if (!r.job_number && r.status !== 'ignored') c['missing-job'] += 1;
      if (r.status === 'ignored') c.ignored += 1;
      if (r.job_number && !r.has_freight) c['no-freight'] += 1;
      if (r.job_number && !r.has_ace) c['no-ace'] += 1;
    }
    return c;
  }, [rows]);

  const q = search.trim().toUpperCase();
  const shown = useMemo(() => (rows || [])
    .filter((r) => filter === 'all'
      || (filter === 'missing-job' ? (!r.job_number && r.status !== 'ignored')
        : filter === 'ignored' ? r.status === 'ignored'
          : filter === 'no-freight' ? (r.job_number && !r.has_freight)
            : (r.job_number && !r.has_ace)))
    .filter((r) => !q || `${r.comm_inv} ${r.job_number || ''} ${r.lookup_spelling || ''} ${r.div || ''} ${r.warehouse || ''} ${r.prod_type || ''} ${r.ignore_reason || ''}`.toUpperCase().includes(q)),
  [rows, filter, q]);

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600">
        Every commercial invoice received in your Actual Cost scope, with its broker job# and
        whether that job has ACE and freight data. Fill in missing job#s here — saved as an exact link under the
        ERP spelling. Small / prepaid / parcel invoices that will never get a broker job# can be
        <em> ignored</em> instead (reversible — see the Ignored filter). Freight fills in per job via the
        Uploads tab's forwarding-invoice list (List 2). The dashboard picks edits up on its next Run; the
        Power BI mirror on the hourly sync.
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(([id, label]) => (
          <button key={id} onClick={() => setFilter(id)}
            className={`rounded-full px-3 py-1 text-xs font-medium border ${filter === id ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>
            {label} <span className="opacity-70">{counts[id] ?? 0}</span>
          </button>
        ))}
        <input
          type="search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search invoice / job#…"
          className="ml-auto w-56 border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
        />
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 text-sm font-medium px-2.5 py-1 rounded">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </button>
      </div>

      {error && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{error}</div>}
      {loading && !rows && <div className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading received invoices…</div>}

      {rows && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="text-slate-500 text-xs bg-slate-50">
              <tr>{['Commercial invoice (ERP)', 'Last received', 'Div', 'WH', 'Type', 'Units', 'FOB', 'Job#', 'Linked via', 'ACE', 'Freight'].map((h, i) => <th key={i} className="text-left font-medium px-3 py-2">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map((r) => (
                <tr key={r.comm_inv} className="hover:bg-slate-50">
                  <td className="px-3 py-1.5 font-mono text-xs">{r.comm_inv}</td>
                  <td className="px-3 py-1.5 text-xs text-slate-500">{r.last_recv || '—'}</td>
                  <td className="px-3 py-1.5 text-xs text-slate-500">{r.div || '—'}</td>
                  <td className="px-3 py-1.5 text-xs text-slate-500">{r.warehouse || '—'}</td>
                  <td className="px-3 py-1.5 text-xs text-slate-500">{r.prod_type || '—'}</td>
                  <td className="px-3 py-1.5 tabular-nums text-slate-600">{(r.units || 0).toLocaleString()}</td>
                  <td className="px-3 py-1.5 tabular-nums text-slate-600">{money(r.fob)}</td>
                  <td className="px-3 py-1.5 font-mono text-xs">
                    {editInv === r.comm_inv ? (
                      <span className="inline-flex items-center gap-1.5">
                        <input
                          value={jobInput} onChange={(e) => setJobInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveJob(r.comm_inv, r); }}
                          placeholder="B00…" autoFocus
                          className="w-32 border border-slate-300 rounded px-1.5 py-0.5 font-mono uppercase focus:outline-none focus:ring-1 focus:ring-teal-500"
                        />
                        <button onClick={() => saveJob(r.comm_inv, r)} disabled={saving || !jobInput.trim()} title="Save" className="text-teal-700 hover:text-teal-800 disabled:opacity-40"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setEditInv(null); setJobInput(''); }} title="Cancel" className="text-slate-500 hover:text-slate-700"><X className="h-4 w-4" /></button>
                      </span>
                    ) : r.job_number ? (
                      <button onClick={() => { setEditInv(r.comm_inv); setJobInput(r.job_number); }} title="Edit job#" className="hover:text-teal-700">{r.job_number}</button>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        <button onClick={() => { setEditInv(r.comm_inv); setJobInput(''); }} className="text-amber-700 hover:text-amber-800 font-medium text-xs">+ set job#</button>
                        {/* CHA-379 — dismiss a small/prepaid/parcel invoice instead of linking it */}
                        {r.status !== 'ignored' && (
                          <IgnoreControl inv={r.comm_inv} reasons={ignoreReasons || []} tenantId={tenantId} onDone={load} onError={setError} />
                        )}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-xs">
                    {r.link_via === 'exact' && <span className="inline-block rounded px-1.5 py-0.5 text-[11px] font-medium bg-green-100 text-green-800">exact</span>}
                    {r.link_via === 'alias' && <span title={`Matched C-Air's spelling ${r.lookup_spelling} via the alias rules — set the job# here to pin an exact link`} className="inline-block rounded px-1.5 py-0.5 text-[11px] font-medium bg-blue-100 text-blue-800 cursor-help">alias: {r.lookup_spelling}</span>}
                    {/* CHA-379 — dismissed invoice: reason badge + un-ignore (returns it to the worklist) */}
                    {!r.link_via && r.status === 'ignored' && (
                      <span className="inline-flex items-center gap-1.5">
                        <span title={r.ignore_note ? `Note: ${r.ignore_note}` : undefined} className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium bg-slate-200 text-slate-600 ${r.ignore_note ? 'cursor-help' : ''}`}>ignored{r.ignore_reason ? `: ${r.ignore_reason}` : ''}</span>
                        <button onClick={() => unignore(r.comm_inv)} className="text-teal-700 hover:text-teal-800 text-[11px] font-medium">un-ignore</button>
                      </span>
                    )}
                    {!r.link_via && r.status !== 'ignored' && <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-1.5">{r.job_number ? (r.has_ace ? <span className="text-green-700">✓</span> : <span title="Job has no ACE entry yet — duty/tariff unknown" className="text-amber-700 cursor-help">—</span>) : <span className="text-slate-300">·</span>}</td>
                  <td className="px-3 py-1.5">{r.job_number ? (r.has_freight ? <span className="text-green-700">✓</span> : <span title="Job has no forwarding invoice yet — upload List 2 to fill freight" className="text-amber-700 cursor-help">—</span>) : <span className="text-slate-300">·</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!shown.length && <div className="p-4 text-sm text-slate-500">No invoices match this filter.</div>}
        </div>
      )}
    </div>
  );
}
